/** Loading phase understood by the SSR scheduler. */
export type DeferPhase = 'after-lcp' | 'conversion' | 'idle';

/**
 * Outcome of the on-demand Intercom loader load, written by `__loadIntercom`.
 *
 * Read by `useIntercom` when the messenger fails to open: `failed` names the
 * cause outright, `pending` with no `onShow` is a slow network. `loaded` only
 * says the loader from `widget.intercom.io` ran; the messenger itself comes in
 * a second stage from `js.intercomcdn.com`. The loader sets `Intercom.booted`
 * before that stage is even requested, so it cannot tell the two apart — the
 * presence of the `.intercom-messenger-frame` element can, and `useIntercom`
 * reports it as `messengerFrame`.
 */
export interface IntercomScriptState {
  status: 'pending' | 'loaded' | 'failed';
  /** Milliseconds from the insert to `onload` or `onerror`; absent while pending. */
  loadMs?: number;
}

/** Dispatched on `document` by `__loadIntercom` when the widget bundle fails to load. */
export const INTERCOM_SCRIPT_FAILED_EVENT = 'intercom:script-failed';

declare global {
  interface Window {
    /**
     * Shared third-party scheduler defined by the SSR bootstrap
     * (`tp-defer-bootstrap` in server.js). Runs each registered callback once,
     * when its phase fires:
     * - `after-lcp` (default) waits for two signals: the hero image — the first
     *   `img[fetchpriority="high"]` element — finishing (or failing), and the
     *   browser's first contentful paint. The hero says the connection is
     *   free; the paint says the main thread has drawn something. Waiting on
     *   the hero alone starts the queue while hydration is still running and
     *   pushes FCP out; waiting on paint alone ignores the download the hero
     *   is still making. As for the hero image itself,
     *   because that image, not an abstract "largest paint", is what the
     *   deferred downloads must not compete with. A document whose markup has
     *   no such image falls back to Largest Contentful Paint candidates,
     *   debounced 500 ms after the last one; browsers without LCP support
     *   fall back to a flat 4 s timer. An 8 s ceiling caps every path, so
     *   nothing can starve the queue.
     * - `conversion` shares that trigger but is capped at 4 s instead. It
     *   carries the vendor tags whose first hit is the pageview attribution
     *   is built from, and their inline stubs buffer calls in page memory
     *   only: a visitor who leaves before the vendor script lands is never
     *   counted.
     * - `idle` is armed only after `after-lcp` has fired, then waits for
     *   `requestIdleCallback` (4 s timeout).
     * The first user interaction (pointerdown/keydown/touchstart — not scroll,
     * which fires exactly while the user is interacting) resolves the two
     * hero-driven phases early, but deliberately not `idle`: pulling two
     * megabytes of session replay into a tap is the worst moment for INP.
     */
    __deferTP?: (callback: () => void, phase?: DeferPhase) => void;
    /** Starts the hero watch; called by an inline script at the end of body. */
    __tpScanHero?: () => void;
    /**
     * Lazily injects the Intercom widget bundle. Defined by SSR. Idempotent
     * while the bundle is pending or loaded; a failed load resets it so the
     * next tap inserts a fresh tag.
     */
    __loadIntercom?: () => void;
    /** Status of the latest `__loadIntercom` call; absent until the first one. */
    __intercomScript?: IntercomScriptState;
  }
}

const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'touchstart'] as const;

/**
 * Ceilings for the local fallback, mirroring the server's rather than being an
 * independent guess. No hero or LCP signal is available on that path, so these
 * are the only honest numbers to reuse.
 */
const FALLBACK_TIMEOUT_MS: Record<DeferPhase, number> = {
  conversion: 4000,
  'after-lcp': 8000,
  idle: 8000,
};

/**
 * Schedules non-critical third-party work for the given phase, keeping heavy
 * analytics, marketing and support work off the main thread during the
 * critical load window. The callback runs exactly once, and never on the
 * server — an SSR caller gets a silent no-op.
 *
 * On every real request the SSR bootstrap (`window.__deferTP`) is already
 * there: `tp-defer-bootstrap` is a non-deferred inline script in `<head>` and
 * runs before this deferred module does, so the call is scheduled by the same
 * hero-driven logic as every third-party snippet, and the phase means exactly
 * what its JSDoc above says. The local branch below is a defensive fallback
 * for a path that does not currently exist in this app (nothing imports
 * `entry-client.tsx`, the only caller, outside the SSR-rendered document). It
 * cannot distinguish the phases by trigger — without the bootstrap there is no
 * hero to watch — so it honours only their ceilings.
 */
export const runWhenIdleOrInteraction = (
  callback: () => void,
  phase: DeferPhase = 'after-lcp',
): void => {
  if (typeof window === 'undefined') return;

  if (typeof window.__deferTP === 'function') {
    window.__deferTP(callback, phase);
    return;
  }

  const timeout = FALLBACK_TIMEOUT_MS[phase];
  let fired = false;

  const run = () => {
    if (fired) return;
    fired = true;
    for (const event of INTERACTION_EVENTS) {
      window.removeEventListener(event, run);
    }
    callback();
  };

  for (const event of INTERACTION_EVENTS) {
    window.addEventListener(event, run, { once: true, passive: true });
  }

  // requestIdleCallback fires on the first idle slice, which arrives long
  // before the load settles; only the timeout is a meaningful floor here.
  window.setTimeout(run, timeout);
};
